import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { ReservationTypeTableColumns } from 'src/features/reservations/components/tables/utils/reservationsTypeTableColumns';
import { useReservationTypeCurrentVersion } from 'src/features/reservations/hooks/useReservationTypeCurrentVersion';
import { RESERVATION_TYPE, type ReservationType } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { getDefaultReservationType } from 'src/features/reservations/utils/utils';
import { DataTable } from 'src/registry/components/table';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { useTitleStore } from 'src/stores/useTitleStore';

const DynamicReservationsTypeTable = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const { parkingId } = useParams();
    const { setTitle, removeTitle } = useTitleStore();

    const rowSelection = useRowSelection({ sidePanelName: 'RESERVATION_DETAILS_SIDE_PANEL' });

    const { data } = useGetParking({
        parkingId,
    });

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return removeTitle;
    }, [data, formatMessage]);

    const { data: hourlyReservationType } = useReservationTypeCurrentVersion({
        parkingId,
        reservationType: RESERVATION_TYPE.HOURLY,
        includeDisabled: true,
    });

    const { data: dailyReservationType } = useReservationTypeCurrentVersion({
        parkingId,
        reservationType: RESERVATION_TYPE.DAILY,
        includeDisabled: true,
    });

    const dataTable = React.useMemo(() => {
        const daily = dailyReservationType
            ? [dailyReservationType]
            : [getDefaultReservationType(RESERVATION_TYPE.DAILY)];
        const hourly = hourlyReservationType
            ? [hourlyReservationType]
            : [getDefaultReservationType(RESERVATION_TYPE.HOURLY)];
        return {
            count: daily.length + hourly.length,
            results: [...hourly, ...daily] as ReservationType[],
        };
    }, [dailyReservationType, hourlyReservationType]);

    const columns = ReservationTypeTableColumns();

    return (
        <DataTable
            columns={columns}
            data={dataTable}
            rowSelection={rowSelection}
            semantics={{
                title: formatMessage(reservationMessages.reservationPlans),
                entryName: formatMessage(reservationMessages.plan),
                entryNames: formatMessage(reservationMessages.plans),
            }}
            dataTableBodyProps={{
                onRowClickCallback: (row) => {
                    void navigate(`${row.reservation_type}`);
                },
            }}
            enableLocalFilters={true}
        />
    );
};

export default DynamicReservationsTypeTable;
